import React, { useState, useEffect } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import DefaultButton from "../../../components/buttons/defaultButton";
import DefaultInput from "../../../components/inputs/defaultInput";
import DefaultDatePicker from "../../../components/inputs/defaultDatePicker";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { FiDownload } from "react-icons/fi";
import { toast } from "react-hot-toast";
import moment from "moment";

const ReportPageTemplate = ({
  title,
  breadcrumbs,
  fetchFunction,
  exportFunction,
  tableHeaders,
  dataTransformer,
  filters = [],
  charts = [],
}) => {
  // Initialize filter values with default dates if date filters are required
  const getInitialFilterValues = () => {
    const initial = {};
    filters.forEach((filter) => {
      if (filter.type === "date") {
        // Set default date range: start date = 1st of current month, end date = end of current month
        if (filter.key === "date_start") {
          initial[filter.key] = moment().startOf("month").format("YYYY-MM-DD");
        } else if (filter.key === "date_end") {
          initial[filter.key] = moment().endOf("month").format("YYYY-MM-DD");
        }
      }
    });
    return initial;
  };

  const [filterValues, setFilterValues] = useState(getInitialFilterValues());
  // Show filters by default if date filters are present
  const hasDateFilters = filters.some((f) => f.type === "date");

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    handleSearchChange,
    handlePageChange,
    fetchData,
  } = useCommonFetch(fetchFunction, {
    dataPath: "data.data",
    totalPath: "data.meta.total",
    errorMessage: `Failed to fetch ${title}!`,
    debounceDelay: 500,
    additionalParams: filterValues, // Pass filterValues (including date_start, date_end) as additionalParams
    dataTransformer,
    autoFetch: hasDateFilters ? (filterValues.date_start && filterValues.date_end) : true, // Only auto-fetch if dates are set
  });

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    if (!exportFunction) {
      toast.error("Export not available for this report");
      return;
    }

    try {
      const blob = await exportFunction(filterValues);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title.toLowerCase().replace(/\s+/g, "_")}_${moment().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Report exported successfully!");
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        // isSearch
        // onSearchChange={handleSearchChange}
        breadcrumbs={breadcrumbs}
      />

      {/* Charts Section */}
      {charts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {charts.map((ChartComponent, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <ChartComponent filters={filterValues} />
            </div>
          ))}
        </div>
      )}

      {/* Filters & Export */}
      <div className="bg-white rounded-lg">
        <div className="flex flex-row justify-between items-center gap-4">
          {hasDateFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filters
                  .filter((f) => f.type === "date")
                  .map((filter) => (
                    <DefaultDatePicker
                      key={filter.key}
                      name={filter.key}
                      label={filter.label}
                      value={filterValues[filter.key] || ""}
                      onChange={(date) =>
                        handleFilterChange(filter.key, date)
                      }
                      placeholder={filter.label}
                      maxDate={moment().toDate()}
                      classnames="w-80"
                    />
                  ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end">
            {/* <DefaultButton
              name="Export CSV"
              variant="primary"
              size="sm"
              onClick={handleExport}
              icon={<FiDownload />}
            /> */}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          TableHeaderData={tableHeaders}
          tabledata={tableData.data || []}
          currentPage={currentPage}
          totalData={totalData}
          setCurrentPage={handlePageChange}
          loading={loading}
          showActions={false}
          action={
            tableData?.action &&
            typeof tableData.action === "object" &&
            Object.keys(tableData.action).length > 0
              ? tableData.action
              : null
          }
        />
      </div>
    </div>
  );
};

export default ReportPageTemplate;

